import { useState } from 'react';
import { Alert } from 'reactstrap';
import { toast } from 'react-toastify';
import { colors, layout } from '../NewHire/v2/styles';
import { PayRatesForm } from '../../../components/Employee/PayRates';
import AnnualReviewService from '../../../services/AnnualReview';
import { AnnualReviewResponse } from './types';

interface PayRaiseStepProps {
  review: AnnualReviewResponse;
  token: string;
  onClose: () => void;
  onBound: (updated: AnnualReviewResponse) => void;
  onSkip: () => void;
}

const extractPayRateId = (response: any): string | null => {
  if (!response) return null;
  if (typeof response === 'object') {
    if (response?.data?.id) return response.data.id;
    if (response?.id) return response.id;
  }
  return null;
};

const PayRaiseStep = ({ review, token, onClose, onBound, onSkip }: PayRaiseStepProps) => {
  const [binding, setBinding] = useState(false);

  const handlePayRateSubmit = async (response: any) => {
    const payRateId = extractPayRateId(response);
    if (!payRateId) {
      toast.error('Pay rate was created but its id could not be read. Refresh and try again.');
      return;
    }
    setBinding(true);
    try {
      const res = await AnnualReviewService(token).bindPayRate(review.id, payRateId);
      toast.success('Pay raise request sent to Finance for approval.');
      onBound(res.data);
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Could not link the pay raise to this annual review.');
    } finally {
      setBinding(false);
    }
  };

  if (!review.employeeId) {
    return (
      <div>
        <p style={{ color: colors.danger }}>Cannot start pay raise — missing employee data on this annual review.</p>
        <button type="button" onClick={onClose} style={layout.secondaryBtn}>
          Back
        </button>
      </div>
    );
  }

  return (
    <div>
      <h1 style={{ ...layout.title, marginBottom: 4 }}>Pay Raise — {review.cycleYear}</h1>
      <p style={layout.subtitle as any}>
        Fill out the pay raise request for {review.employeeName} below. After saving, Finance will review and approve or
        deny. The annual review's combined PDF will reflect the final decision before signatures.
      </p>

      <Alert color="info" className="mt-3">
        <i className="fas fa-info-circle mr-2" />
        This pay raise request is part of <strong>{review.employeeName}'s</strong> Annual Review for{' '}
        <strong>{review.cycleYear}</strong>. After Finance decides, the combined PDF will be updated with the outcome
        and you'll be able to print and sign.
      </Alert>

      {binding ? (
        <p style={{ color: colors.muted, fontStyle: 'italic' }}>Linking pay raise to the annual review…</p>
      ) : null}

      <PayRatesForm
        id={review.employeeId}
        defaultReason="Annual"
        defaultPayRate={review.proposedPayRate?.newPayRate ?? undefined}
        onSubmit={handlePayRateSubmit}
      />

      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 16, gap: 12 }}>
        <button type="button" onClick={onClose} style={layout.secondaryBtn}>
          Back to Performance Management
        </button>
        <button
          type="button"
          onClick={onSkip}
          style={layout.secondaryBtn}
          title="No pay raise this cycle — skip and continue to Growth Plan"
        >
          Skip Pay Raise & Continue
        </button>
      </div>
    </div>
  );
};

export default PayRaiseStep;
